import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  effect,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from '../../shared/services/project/project.service';
import {
  FullProject,
  ProjectProjectIdBody,
  ProjectVersion,
  Workflow,
} from '@app/shared/interfaces';
import { VersionService } from '../../shared/services/version/version.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import {
  AppStateService,
  ViewMode,
} from '../../shared/services/app-state/app-state.service';
import { LocalforageService } from '../../shared/services/localforage/localforage.service';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { VersionDialogData } from './version-dialog/version-dialog.component';
import {
  VersionDropdownComponent,
  VersionSelectEvent,
} from '../../components/version-dropdown/version-dropdown.component';
import {
  WorkflowDropdownComponent,
  WorkflowSelectEvent,
} from '../../components/workflow-dropdown/workflow-dropdown.component';
import {
  SearchAutocompleteComponent,
  SearchResult,
} from '../../components/search-autocomplete/search-autocomplete.component';
import { ProjectLockComponent } from '../../components/project-lock/project-lock.component';
import { ProjectWatchComponent } from '../../components/project-watch/project-watch.component';
import { ProfileComponent } from '../../components/profile/profile.component';
import { MessageService } from '../../shared/services/message/message.service';
import { AppEventBus } from '../../shared/services/events/app-events';
import {
  API_BASE,
  RELOAD_VERSIONS,
} from '../../shared/constants/general.constants';
import { WorkflowService } from '../../shared/services/workflow/workflow.service';
import { LocalstorageHelper } from '../../shared/helpers/localstorage.helper';
import {
  getMostRecentPublishedVersion,
  getVisibleVersionsForRole,
  hasPublishedVersions as hasPublishedProjectVersions,
  isOperatorRole,
  NO_PUBLISHED_VERSIONS_MESSAGE,
} from '../../shared/helpers/version-visibility.helper';

// Storage key for view mode
const VIEW_MODE_STORAGE_KEY = 'project_view_mode';

@Component({
  selector: 'app-project-header',
  templateUrl: './project-header.component.html',
  styleUrls: ['./project-header.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule,
    MatDialogModule,
    MatInputModule,
    MatFormFieldModule,
    MatCheckboxModule,
    FormsModule,
    VersionDropdownComponent,
    WorkflowDropdownComponent,
    SearchAutocompleteComponent,
    ProjectLockComponent,
    ProjectWatchComponent,
    ProfileComponent,
  ],
})
export class ProjectHeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private projectService = inject(ProjectService);
  private versionService = inject(VersionService);
  private toastr = inject(ToastrService);
  private localforageService = inject(LocalforageService);
  private readonly dialogService = inject(DialogService);
  private messageService = inject(MessageService);
  private appEventBus = inject(AppEventBus);
  private notebookService = inject(WorkflowService);
  private http = inject(HttpClient);

  projectId = signal(0);
  projectName = signal('New Project');
  currentVersion = signal('Head');
  currentWorkflow = signal('New Workflow');
  isEditingName = signal(false);
  editedName = signal('');
  project = signal<FullProject>({} as FullProject);
  allProjectVersions = signal<ProjectVersion[]>([]);
  projectVersions = signal<ProjectVersion[]>([]);
  readonly noPublishedVersionsMessage = NO_PUBLISHED_VERSIONS_MESSAGE;
  isSaving = signal(false);
  selectedVersionId = signal<number | undefined>(undefined);
  selectedWorkflowId = signal<number | undefined>(undefined);
  private messageSubscription: Subscription = new Subscription();
  private queryParamsSubscription: Subscription = new Subscription();

  // View mode properties
  viewMode = signal<ViewMode>(ViewMode.FULL);
  viewModeEnum = ViewMode; // Expose enum to template

  // Search functionality
  searchQuery = signal('');

  // AppState
  private appStateService = inject(AppStateService);

  constructor() {
    // Watch for workflow changes in app state and update display
    effect(() => {
      const workflow = this.appStateService.workflow();
      if (workflow && workflow.name) {
        this.currentWorkflow.set(workflow.name);
        this.selectedWorkflowId.set(workflow.workflowId);
      }
    });

    // Keep the version header in sync whenever the active version OR the
    // visible version list changes.  This covers the case where a new frozen
    // version is created: the backend renames the old HEAD versionId in-place
    // (major/minor change), so `projectVersions()` is updated with the new
    // major.minor while `appStateService.version()` still points to the same
    // versionId — the header must immediately reflect the new display name
    // (e.g. "v1.0") without waiting for a URL change that might never come.
    effect(() => {
      const version = this.appStateService.version();
      const versions = this.projectVersions();
      if (version && versions.length > 0) {
        const displayVersion = versions.find(v => v.versionId === version.versionId);
        if (displayVersion) {
          this.setVersionDisplay(displayVersion);
        }
      }
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      const id = params['projectId'];
      if (id) {
        this.projectId.set(+id);
        this.loadProject(this.projectId());
        this.loadViewMode();
        this.setupMessageSubscription();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
  }

  private setupMessageSubscription(): void {
    // `route.params` re-fires when navigating between projects on the same
    // component instance; tear the previous aggregate down before re-binding
    // so child subscriptions (MessageService + AppEventBus) don't accumulate.
    this.messageSubscription.unsubscribe();
    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message?.text === RELOAD_VERSIONS && message?.data) {
          // Handle both old and new message formats
          const data = message.data;

          if (data.project) {
            // New format: contains both project and response
            this.loadProjectAfterVersionCreation(data.project, data.response);
          } else {
            // Old format: just response data
            this.loadProjectAndNavigateToVersion(this.projectId(), data);
          }
        }
      });

    // Server-driven live refresh now arrives via the typed AppEventBus
    // (SCRUM-750). A published version (`reloadVersions`) or a project
    // name/description change (`refreshProject`) both refresh the header
    // without altering the current selection.
    this.messageSubscription.add(
      this.appEventBus
        .on('reloadVersions')
        .subscribe(() => this.refreshProjectDataOnly()),
    );
    this.messageSubscription.add(
      this.appEventBus
        .on('refreshProject')
        .subscribe(() => this.refreshProjectDataOnly()),
    );
  }

  /**
   * Reload project data when version changes to ensure we have the latest version list
   */
  private reloadProjectData(): void {
    if (this.projectId()) {
      // Invalidate cache first to ensure fresh data
      this.projectService.invalidateProjectCache(this.projectId());
      // Reload project data
      this.loadProject(this.projectId());
    }
  }

  /**
   * Refresh project data (name, versions) without changing the selected version.
   * Used by live data push notifications.
   */
  private refreshProjectDataOnly(): void {
    if (!this.projectId()) return;
    // Fetch fresh project data directly via HTTP — avoid getProjectFresh() to
    // prevent localforage side-effects that could trigger cascading refreshes.
    this.http.get<any>(`${API_BASE}/project/${this.projectId()}`).subscribe({
      next: (project) => {
        // Preserve current selection
        const savedVersionId = this.selectedVersionId();
        const savedCurrentVersion = this.currentVersion();

        // Update only the display data
        this.projectName.set(project.name);
        this.updateVersionCollections(project.versions || []);

        // Restore the previous selection if it still exists
        if (savedVersionId) {
          const stillExists = this.projectVersions().find(
            (v) => v.versionId === savedVersionId,
          );
          if (stillExists) {
            this.selectedVersionId.set(savedVersionId);
            this.currentVersion.set(savedCurrentVersion);
          }
        }
      },
      error: (error) => {
        this.handleError('Error refreshing project data', error);
      },
    });
  }

  loadProject(projectId: number): void {
    this.projectService.getProject(projectId).subscribe({
      next: (project) => {
        this.project.set(project);
        this.projectName.set(project.name);
        this.updateVersionCollections(project.versions || []);

        // Always prioritize URL parameter - this ensures the dropdown shows the correct version
        // even if project data is stale
        const versionIdFromQuery = this.route.snapshot.queryParams['versionId'];
        const versionId = versionIdFromQuery
          ? +versionIdFromQuery
          : project.currentVersionId;

        if (versionId) {
          const selectedVersion = this.projectVersions().find(
            (v) => v.versionId === versionId,
          );
          if (selectedVersion) {
            this.setVersionDisplay(selectedVersion);
          } else {
            // Version not found in current projectVersions - this might be stale data
            // Reload project data to get fresh versions list
            console.log(
              'Version not found in projectVersions, reloading project data:',
              {
                versionId,
                availableVersions: this.projectVersions().map((v) => ({
                  id: v.versionId,
                  version: `${v.majorVersion}.${v.minorVersion}`,
                })),
              },
            );
            this.projectService.invalidateProjectCache(projectId);
            this.projectService.getProjectFresh(projectId).subscribe({
              next: (freshProject) => {
                this.project.set(freshProject);
                this.updateVersionCollections(freshProject.versions || []);
                const foundVersion = this.projectVersions().find(
                  (v) => v.versionId === versionId,
                );
                if (foundVersion) {
                  this.setVersionDisplay(foundVersion);
                } else {
                  this.applyVersionSelection(
                    undefined,
                    freshProject.currentVersionId,
                  );
                }
              },
              error: (error) => {
                console.error('Error reloading project data:', error);
                this.applyVersionSelection(undefined, project.currentVersionId);
              },
            });
          }
        } else {
          this.applyVersionSelection(undefined, project.currentVersionId);
        }

        // Also subscribe to query param changes to update when version changes via navigation
        // Unsubscribe from previous subscription to prevent memory leaks
        if (this.queryParamsSubscription) {
          this.queryParamsSubscription.unsubscribe();
        }
        this.queryParamsSubscription = this.route.queryParams.subscribe(
          (queryParams) => {
            // Always prioritize URL parameter - use current project state, not closure variable
            const queryVersionId = queryParams['versionId']
              ? +queryParams['versionId']
              : this.project().currentVersionId;
            if (queryVersionId && queryVersionId !== this.selectedVersionId()) {
              const selectedVersion = this.projectVersions().find(
                (v) => v.versionId === queryVersionId,
              );
              if (selectedVersion) {
                this.setVersionDisplay(selectedVersion);
              } else {
                // Version not found in current projectVersions - reload to get fresh data
                console.log(
                  'Query param version not found, reloading project data:',
                  {
                    queryVersionId,
                    currentSelectedVersionId: this.selectedVersionId(),
                  },
                );
                this.projectService.invalidateProjectCache(projectId);
                this.projectService.getProjectFresh(projectId).subscribe({
                  next: (freshProject) => {
                    this.project.set(freshProject);
                    this.updateVersionCollections(freshProject.versions || []);
                    const foundVersion = this.projectVersions().find(
                      (v) => v.versionId === queryVersionId,
                    );
                    if (foundVersion) {
                      this.setVersionDisplay(foundVersion);
                    } else {
                      this.applyVersionSelection(
                        undefined,
                        freshProject.currentVersionId,
                      );
                    }
                  },
                  error: (error) => {
                    console.error(
                      'Error reloading project data from query params:',
                      error,
                    );
                  },
                });
              }
            }
          },
        );
      },
      error: (error) => {
        this.handleError('Error loading project', error);
      },
    });
  }

  /**
   * Loads project data after version creation and navigates to head version (0.0)
   * @param project The updated project data (may come from message)
   * @param responseData The version response data from API
   */
  private loadProjectAfterVersionCreation(
    project: FullProject | null,
    responseData: any,
  ): void {
    const projectId = this.projectId();

    // Invalidate cache first
    this.projectService.invalidateProjectCache(projectId);

    // If project data provided, use it, otherwise fetch fresh
    if (project) {
      this.updateProjectData(project, responseData);
    } else {
      this.projectService.getProjectFresh(projectId).subscribe({
        next: (freshProject) => {
          this.updateProjectData(freshProject, responseData);
        },
        error: (error) => {
          this.handleError('Error reloading project versions', error);
        },
      });
    }
  }

  /**
   * Updates project data for version dropdown
   * Navigation and selectedVersionId are handled by queryParams subscription
   */
  private updateProjectData(project: FullProject, _responseData: any): void {
    // Update project data and versions list for dropdown
    this.project.set(project);
    this.projectName.set(project.name);
    this.updateVersionCollections(project.versions || []);

    console.log('✅ UI updated with new versions list:', {
      versionsCount: this.projectVersions().length,
      currentVersionId: project.currentVersionId,
    });

    // Note: selectedVersionId and navigation are handled by:
    // 1. version-dialog PHASE 5 calls navigateToVersion()
    // 2. queryParams subscription updates selectedVersionId
  }

  /**
   * Loads project data and navigates to the newly created frozen version
   * @param projectId The ID of the project to reload
   * @param versionData The version response data from API (may include createdVersionMajor/Minor)
   */
  private loadProjectAndNavigateToVersion(
    projectId: number,
    versionData: any,
  ): void {
    // First invalidate the cache to ensure we get fresh data
    this.projectService.invalidateProjectCache(projectId);

    // Use getProjectFresh to bypass cache and get updated data
    this.projectService.getProjectFresh(projectId).subscribe({
      next: (project) => {
        // Update project data and versions
        this.project.set(project);
        this.projectName.set(project.name);
        this.updateVersionCollections(project.versions || []);

        // Try to find the created version using major/minor from the versionData
        let createdVersion: ProjectVersion | undefined;

        if (
          versionData?.createdVersionMajor !== undefined &&
          versionData?.createdVersionMinor !== undefined
        ) {
          createdVersion = this.projectVersions().find(
            (v) =>
              v.majorVersion === versionData.createdVersionMajor &&
              v.minorVersion === versionData.createdVersionMinor,
          );
        }

        // Fallback to latestVersionId if exact match not found
        if (!createdVersion && project.latestVersionId) {
          createdVersion = this.projectVersions().find(
            (v) => v.versionId === project.latestVersionId,
          );
        }

        if (createdVersion) {
          this.setVersionDisplay(createdVersion);

          // Navigate to the frozen version that was just created
          this.appStateService.navigateToVersion(createdVersion.versionId);
          console.log(
            'Successfully navigated to newly created version:',
            createdVersion,
          );
        } else {
          console.warn(
            'Created version not found, falling back to head version',
          );
          // Fallback to head version if created version not found
          const headVersion = this.projectVersions().find(
            (v) => v.versionId === project.currentVersionId,
          );
          if (headVersion) {
            this.setVersionDisplay(headVersion);
            this.appStateService.navigateToVersion(headVersion.versionId);
          }
        }
      },
      error: (error) => {
        this.handleError('Error reloading project versions', error);
      },
    });
  }

  goBack(): void {
    // SCRUM-181: Explicitly clear query parameters when navigating back to projects list
    this.router.navigate(['/projects'], {
      queryParams: {},
    });
  }

  selectVersion(versionName: string, versionId?: number): void {
    if (!versionId) {
      console.warn('⚠️ selectVersion called without versionId');
      return;
    }

    // Don't check projectId here - let navigateToVersion handle it
    // Component may have stale projectId, but AppState may have current one

    console.log('🔄 Version selection started:', { versionName, versionId });

    // Immediately update UI for instant feedback
    // If navigation fails or guard redirects, queryParams subscription will correct UI to actual version
    const selectedVersion = this.projectVersions()?.find(
      (v) => v.versionId === versionId,
    );
    if (selectedVersion) {
      this.setVersionDisplay(selectedVersion);
    }

    // Navigate to the selected version - this will update the URL with the new versionId
    // The URL will be updated to: /project/{projectId}?versionId={versionId}&workflowId={workflowId}
    this.appStateService
      .navigateToVersion(versionId)
      .then((success) => {
        console.log('✅ Version navigation completed:', {
          success,
          currentUrl: this.router.url,
          expectedVersionId: versionId,
        });

        if (success) {
          // Save to localStorage ONLY after successful navigation
          LocalstorageHelper.setLastInteractedVersion(
            this.projectId(),
            versionId,
          );
        } else {
          console.warn('⚠️ Version navigation failed or redirected');
          // UI already updated by queryParams subscription to actual version
          this.toastr.error('Failed to switch version. Please try again.');
        }
      })
      .catch((error) => {
        console.error('❌ Version navigation error:', error);
        this.toastr.error(
          'An error occurred while switching versions. Please try again.',
        );
      });
  }

  onVersionSelected(event: VersionSelectEvent): void {
    this.selectVersion(event.versionName, event.version.versionId);
  }

  onNewVersionRequested(): void {
    this.openVersionDialog();
  }

  openVersionDialog(): void {
    if (!this.validateProjectId()) {
      return;
    }

    const dialogData: VersionDialogData = {
      projectId: this.projectId(),
    };

    this.dialogService.versionDialog(dialogData).subscribe((result) => {
      if (result) {
        // Version creation, reload, and navigation are handled by the RELOAD_VERSIONS message subscription
        console.log('Version dialog closed with result:', result);
      }
    });
  }

  isHeadVersion(version: ProjectVersion): boolean {
    return version.majorVersion === 0 && version.minorVersion === 0;
  }

  /**
   * Check if the currently selected version is a head version
   */
  isCurrentVersionHeadVersion(): boolean {
    if (
      !this.selectedVersionId() ||
      !this.projectVersions() ||
      this.projectVersions().length === 0
    ) {
      return false;
    }

    const currentVersion = this.projectVersions().find(
      (v) => v.versionId === this.selectedVersionId(),
    );
    if (!currentVersion) {
      return false;
    }

    return this.isHeadVersion(currentVersion);
  }
  onWorkflowSelected(event: WorkflowSelectEvent): void {
    console.log('Selected workflow:', event.workflow);

    // Navigate to the selected notebook (workflow) to update app state and reload chunks
    if (event.workflow?.workflowId) {
      // Update the displayed workflow name and ID immediately
      this.currentWorkflow.set(event.workflow.name);
      this.selectedWorkflowId.set(event.workflow.workflowId);

      this.notebookService.setWorkflowId(event.workflow.workflowId);
      // Navigate to the workflow to update app state
      this.appStateService.navigateToWorkflow(event.workflow.workflowId);
    }
  }

  onNewWorkflowRequested(): void {
    console.log('New workflow requested');
    // The dropdown component handles the workflow creation
  }

  onWorkflowDeleted(deletedWorkflowId: number): void {
    if (this.selectedWorkflowId() === deletedWorkflowId) {
      // Navigate to the first remaining workflow or back to the project root
      const remaining = (
        this.notebookService.getNotebookList() as Workflow[]
      ).filter((w) => w.workflowId !== deletedWorkflowId);

      if (remaining.length > 0) {
        const next = remaining[0];
        this.currentWorkflow.set(next.name);
        this.selectedWorkflowId.set(next.workflowId);
        this.notebookService.setWorkflowId(next.workflowId);
        this.appStateService.navigateToWorkflow(next.workflowId);
      } else {
        this.currentWorkflow.set('New Workflow');
        this.selectedWorkflowId.set(undefined);
        this.router.navigate(['/project', this.projectId()]);
      }
    }
  }
  /**
   * Validate project ID exists
   */
  private validateProjectId(): boolean {
    return !!this.projectId();
  }

  /**
   * Common error handler to reduce duplication
   */
  private handleError(message: string, error: any): void {
    console.error(`${message}:`, error);
    this.toastr.error(message);
  }

  // Set view mode and save it
  setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
    // Update app state
    this.appStateService.setViewMode(mode);
    // Still save to local storage for persistence
    this.saveViewMode();
    this.toastr.success(`Switched to ${mode} view`);
  }

  // Check if view mode is active
  isViewModeActive(mode: ViewMode): boolean {
    return this.viewMode() === mode;
  }

  // Legacy method to support existing HTML
  changeView(view: string): void {
    const viewModeMap: { [key: string]: ViewMode } = {
      full: ViewMode.FULL,
      compact: ViewMode.COMPACT,
    };

    const mode = viewModeMap[view] || ViewMode.FULL;
    this.setViewMode(mode);
  }

  // Load view mode from storage
  private async loadViewMode(): Promise<void> {
    try {
      // Use localforage service which now has built-in localStorage fallback
      const storedMode = await this.localforageService.getItem(
        VIEW_MODE_STORAGE_KEY,
      );
      let mode = ViewMode.FULL;

      if (
        storedMode &&
        (storedMode === ViewMode.FULL || storedMode === ViewMode.COMPACT)
      ) {
        mode = storedMode as ViewMode;
      }

      this.viewMode.set(mode);
      // Update app state with stored/default mode
      this.appStateService.setViewMode(mode);
    } catch (error) {
      this.handleError('Error loading view mode', error);
      // Default to FULL view if all else fails
      this.viewMode.set(ViewMode.FULL);
      this.appStateService.setViewMode(ViewMode.FULL);
    }
  }

  // Save view mode to storage
  private async saveViewMode(): Promise<void> {
    try {
      // Use localforage service which now has built-in localStorage fallback
      await this.localforageService.setItem(
        VIEW_MODE_STORAGE_KEY,
        this.viewMode(),
      );
    } catch (error) {
      this.handleError('Error saving view mode', error);
    }
  }

  startEditing(): void {
    this.isEditingName.set(true);
    this.editedName.set(this.projectName());
  }

  saveProjectName(): void {
    if (!this.validateProjectNameEdit()) {
      return;
    }

    this.isSaving.set(true);
    const updateData: ProjectProjectIdBody = {
      name: this.editedName(),
      description: this.project().description,
    };

    this.projectService.updateProject(this.projectId(), updateData).subscribe({
      next: (updatedProject) => {
        this.handleProjectUpdate(updatedProject);
      },
      error: (error) => {
        this.handleError('Error updating project name', error);
        this.isSaving.set(false);
      },
    });
  }

  cancelEditing(): void {
    this.isEditingName.set(false);
  }

  /**
   * Validate project name edit conditions
   */
  private validateProjectNameEdit(): boolean {
    return !!(this.editedName().trim() && !this.isSaving());
  }

  /**
   * Handle successful project update
   */
  private handleProjectUpdate(updatedProject: FullProject): void {
    this.projectName.set(updatedProject.name);
    this.project.set(updatedProject);
    this.isEditingName.set(false);
    this.isSaving.set(false);
  }

  private updateVersionCollections(versions: ProjectVersion[]): void {
    this.allProjectVersions.set(versions);
    this.projectVersions.set(this.getVisibleProjectVersions(versions));
  }

  private getVisibleProjectVersions(
    versions: ProjectVersion[],
  ): ProjectVersion[] {
    return getVisibleVersionsForRole(versions, this.getCurrentProjectRole());
  }

  private getCurrentProjectRole(): string {
    return this.appStateService.userProjectRole() ?? '';
  }

  private isOperatorWithoutPublishedVersions(): boolean {
    return (
      isOperatorRole(this.getCurrentProjectRole()) &&
      !hasPublishedProjectVersions(this.allProjectVersions())
    );
  }

  private applyVersionSelection(
    requestedVersionId?: number,
    currentVersionId?: number,
  ): void {
    const versions = this.projectVersions();
    const requestedVersion = requestedVersionId
      ? versions.find(
          (version) => version.versionId === requestedVersionId,
        )
      : undefined;
    const currentVisibleVersion = currentVersionId
      ? versions.find(
          (version) => version.versionId === currentVersionId,
        )
      : undefined;
    const fallbackVersion =
      requestedVersion ||
      currentVisibleVersion ||
      getMostRecentPublishedVersion(versions) ||
      versions[0];

    if (fallbackVersion) {
      this.setVersionDisplay(fallbackVersion);
      return;
    }

    this.currentVersion.set(this.getVersionEmptyStateMessage());
    this.selectedVersionId.set(undefined);
  }

  getVersionEmptyStateMessage(): string {
    if (this.isOperatorWithoutPublishedVersions()) {
      return this.noPublishedVersionsMessage;
    }
    return 'No versions available';
  }

  private setVersionDisplay(version: ProjectVersion): void {
    this.currentVersion.set(this.getVersionDisplay(version));
    this.selectedVersionId.set(version.versionId);
  }

  getVersionDisplay(version: ProjectVersion): string {
    return `v${version.majorVersion}.${version.minorVersion}`;
  }

  // Search functionality
  onSearch(query: string): void {
    console.log('Search query:', query);
    // Implement search functionality by updating the search query
    this.searchQuery.set(query);
    this.onSearchQueryChange();
  }

  onResultSelected(result: SearchResult): void {
    console.log('Selected result:', result);

    // Navigate based on result type
    if (
      'name' in result &&
      'projectId' in result &&
      result.projectId &&
      !('workflowId' in result) &&
      !('stepId' in result)
    ) {
      // This is a project result
      this.navigateToProject(result.projectId);
    } else if (
      'workflowId' in result &&
      'versionId' in result &&
      result.projectId
    ) {
      // This is a notebook or chunk result
      this.navigateToProject(result.projectId, result.versionId);
    }
  }

  /**
   * Navigate to project with optional version
   */
  private navigateToProject(projectId: number, versionId?: number): void {
    const navigationExtras = versionId ? { queryParams: { versionId } } : {};
    this.router.navigate(['/project', projectId], navigationExtras);
  }

  // Legacy search methods - keeping for backward compatibility
  onSearchQueryChange(): void {
    console.log('Search query changed:', this.searchQuery());
    // Emit search event to parent component or trigger search logic
    if (this.searchQuery().trim()) {
      // Could emit to parent component or call search service
      // For now, just log the search query
      console.log('Searching for:', this.searchQuery().trim());
    }
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.onSearchQueryChange();
  }

  openFilters(): void {
    console.log('Open filters clicked');
    // Implement filter functionality - could open a dialog or sidebar
    // For now, just log that filters are being opened
    // This could trigger a filter panel or dialog to appear
    console.log('Filter functionality would be implemented here');
  }
}
